/**
 * 진행 중인 개선 제안(proposal) 의 ID 를 localStorage 에 보관한다.
 *
 * proposal 전환 후에는 job/target 의 진행 상태를 서버(proposal target)가 보관하므로,
 * 화면은 proposal ID 하나만 기억하면 페이지를 이탈했다 돌아와도 /feedback 에서 최신
 * 상태를 재수화해 이어볼 수 있다. (모달을 닫으면 컴포넌트가, 페이지를 벗어나면 탭까지
 * 사라져 메모리 상태가 날아가지만, proposal ID 만 있으면 복원된다.)
 *
 * 설계 메모:
 * - **사용자별로 분리한다.** 공용 PC 에서 다른 관리자가 로그인했을 때 남의 작업이
 *   보이면 안 된다. 로그인 시 저장되는 user_id 를 키에 넣는다.
 * - **TTL 을 둔다.** 어제 걸어둔 작업이 오늘 갑자기 뜨는 것은 혼란이다. 하루면 충분하다.
 * - **OPEN 을 벗어나면 지운다.** proposal 이 완료/취소되면 핸들은 역할이 끝난 것이다.
 */

const KEY_PREFIX = 'supplementPromptPreviewJob';
// 하루. 이보다 오래된 핸들은 되살리지 않는다(서버 결과는 30일 남아 있지만,
// 어제 걸어둔 작업이 오늘 갑자기 뜨는 것은 기대 동작이 아니다).
const TTL_MS = 24 * 60 * 60 * 1000;

function storageKey() {
    // 로그인 정보가 없으면(로그아웃 등) 익명 키로 떨어뜨려, 다음 로그인 사용자가
    // 이전 사용자의 핸들을 물려받지 않게 한다.
    const userId = localStorage.getItem('user_id') || 'anon';
    return `${KEY_PREFIX}:${userId}`;
}

/**
 * v3: 진행 중 개선 제안(proposal) 의 ID 만 저장한다.
 *
 * proposal 전환 후에는 job/target 의 진행 상태를 서버(proposal target)가 보관하므로,
 * 화면은 proposal ID 하나만 기억하면 /feedback 에서 최신 상태를 재수화할 수 있다.
 * v2 처럼 job 배열/plan/피드백ID 를 중복 저장하지 않는다(서버가 단일 진실).
 */
export function saveActiveProposal(proposalId) {
    if (typeof proposalId !== 'number') return;
    try {
        localStorage.setItem(storageKey(), JSON.stringify({
            version: 3,
            proposalId,
            savedAt: Date.now(),
        }));
    } catch (e) {
        // 시크릿 모드 등에서 localStorage 가 막혀도 기능은 계속 동작한다(이어보기만 못 함).
    }
}

/** 진행 중 proposal ID 조회. 없거나 만료·손상·구버전이면 null(그 값은 정리한다). */
export function loadActiveProposal() {
    try {
        const raw = localStorage.getItem(storageKey());
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || saved.version !== 3
            || typeof saved.proposalId !== 'number'
            || !saved.savedAt || Date.now() - saved.savedAt > TTL_MS) {
            // 구 v2 핸들·손상·만료 값은 남겨두면 매 진입마다 다시 파싱된다 — 지운다.
            clearActiveProposal();
            return null;
        }
        return saved.proposalId;
    } catch (e) {
        clearActiveProposal();
        return null;
    }
}

/** proposal 핸들 삭제 — proposal 이 OPEN 을 벗어났거나 사용자가 배너에서 지울 때. */
export function clearActiveProposal() {
    try {
        localStorage.removeItem(storageKey());
    } catch (e) {
        // 무시 (위와 동일)
    }
}
